import {
  buildContactTag,
  contactPointVerificationLabel,
  type ContactPointLike,
  type ContactTagPresentation,
} from "../../../../../../_shared/_logic/contact-quality-tags"
import { formatContactAddress } from "../../../../../../_shared/_logic/contact-addresses"
import {
  buildRichTooltip,
  tooltipField,
  tooltipList,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"

const normalize = (value: unknown) => `${value ?? ""}`.trim()
const upper = (value: unknown) => normalize(value).toUpperCase()

const activeContactPoints = (points: ContactPointLike[]) =>
  (points ?? []).filter(
    (point) => !["INVALID", "DO_NOT_USE", "ARCHIVED"].includes(upper(point.lifecycle_status)),
  )

const pointsForKind = (points: ContactPointLike[], kind: string) =>
  activeContactPoints(points).filter((point) => upper(point.kind) === kind)

const valueForPoint = (point: ContactPointLike) => {
  if (upper(point.kind) === "ADDRESS") {
    return formatContactAddress(point.value) || formatContactAddress(point.normalized_value)
  }

  return normalize(point.value) || normalize(point.normalized_value)
}

const bestDigitalLabel = (points: ContactPointLike[]) => {
  const emails = pointsForKind(points, "EMAIL")
  const mobiles = pointsForKind(points, "MOBILE")
  const fixedLines = pointsForKind(points, "FIXED_LINE")
  const addresses = pointsForKind(points, "ADDRESS")

  if (emails.length) return "E-Mail"

  if (mobiles.length) return "Mobil"

  if (fixedLines.length) return "Festnetz"

  if (addresses.length) return "Adresse"

  return "Keine Kontaktdaten"
}

const plannedChannelsForCustomer = ({
  customerId,
  steps,
}: {
  customerId?: string | null
  steps: ContactOrderStep[]
}) => {
  if (!customerId) return []

  const labels = steps
    .filter((step) => step.recipient_customer_id === customerId)
    .map((step) => {
      const channel = upper(step.channel)

      if (channel === "EMAIL") return "E-Mail"

      if (channel === "MOBILE_MESSAGE") return "Mobile Nachricht"

      if (channel === "PHONE") return "Telefon"

      if (channel === "LETTER") return "Terminbrief"

      return normalize(step.channel)
    })
    .filter(Boolean)

  return Array.from(new Set(labels))
}

export const buildRecipientOverviewTag = ({
  id,
  customer,
  fallbackLabel,
  contactPoints,
  roleLabel,
  unitLabel,
  steps,
  contactPointById,
}: {
  id: string
  customer?: Customer | null
  fallbackLabel?: string | null
  contactPoints: ContactPointLike[]
  roleLabel?: string | null
  unitLabel?: string | null
  steps: ContactOrderStep[]
  contactPointById: Record<string, ContactPointLike>
}): ContactTagPresentation => {
  const baseTag = buildContactTag({
    id,
    customer,
    fallbackLabel,
    contactPoints,
    unitLabel,
    roleLabel,
    showRoleInLabel: true,
  })
  const normalizedRole = normalize(roleLabel) || "Nicht hinterlegt"
  const plannedChannels = plannedChannelsForCustomer({
    customerId: id,
    steps,
  })
  const emails = pointsForKind(contactPoints, "EMAIL")
  const mobiles = pointsForKind(contactPoints, "MOBILE")
  const fixedLines = pointsForKind(contactPoints, "FIXED_LINE")
  const addresses = pointsForKind(contactPoints, "ADDRESS")
  const contactSummary = bestDigitalLabel(contactPoints)

  return {
    ...baseTag,
    roleLabel: `${normalizedRole} · ${contactSummary}`,
    tooltip: buildRichTooltip([
      baseTag.tooltip,
      plannedChannels.length ? tooltipField("Geplant", plannedChannels.join(", ")) : null,
    ]),
    tooltipHtml: true,
  }
}
